import * as React from 'react';
import { makeStyles, mergeClasses, shorthands, Text, tokens } from '@fluentui/react-components';
import { defineMessage, useIntl } from 'react-intl';
import { accountLevelLabel, Levels } from '../modules/levels';
import { useAccountRequired } from '../store';

export function LevelProgress() {
  const intl = useIntl();
  const account = useAccountRequired();
  const classes = useStyles();

  let levelMet = false;

  return (
    <div role="list" aria-label={intl.formatMessage(accessibilityLabel)} className={classes.root}>
      {Levels.map((item, i) => {
        const isCurrent = account.account_level === item.slug;
        levelMet = levelMet || isCurrent;
        const isPrevious = !levelMet;
        const isLast = i + 1 === Levels.length;

        const { name, icon: Icon } = item;
        return (
          <div
            key={i + 1}
            className={mergeClasses(classes.level, isLast && classes.levelLast)}
            aria-label={intl.formatMessage(name)}
            role="listitem"
            aria-current={isCurrent ? 'step' : undefined}
          >
            <div className={classes.content}>
              <div
                className={mergeClasses(
                  classes.iconWrap,
                  isCurrent ? classes.wrapCurrent : isPrevious ? classes.wrapPrevious : false
                )}
              >
                <Icon
                  fontSize={20}
                  color={
                    isCurrent
                      ? tokens.colorNeutralForegroundOnBrand
                      : isPrevious
                        ? tokens.colorBrandForeground1
                        : tokens.colorNeutralStrokeAccessible
                  }
                />
              </div>
              <Text className={classes.nameText}>{intl.formatMessage(name)}</Text>
              <Text className={classes.levelText}>{intl.formatMessage(accountLevelLabel, { levelNumber: i + 1 })}</Text>
            </div>
            {!isLast ? (
              <div key={`spacer-${i}`} role="presentation" className={classes.spacer}>
                <div className={mergeClasses(classes.spacerLine, isPrevious && classes.spacerLineCurrent)} />
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

const accessibilityLabel = defineMessage({
  defaultMessage: 'Founders Hub program level indicator',
  description: 'accessibility label for the four-step founders hub journey progress indicator',
});

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'row',
    columnGap: tokens.spacingHorizontalM,
    justifyContent: 'space-between',
  },

  level: {
    display: 'flex',
    flexDirection: 'row',
    columnGap: tokens.spacingHorizontalM,
    flexGrow: 1,
  },
  levelLast: {
    flexGrow: 0,
  },

  content: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
    alignItems: 'center',
  },
  spacer: {
    flexGrow: 1,
    height: '50px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  spacerLine: {
    height: tokens.strokeWidthThin,
    ...shorthands.overflow('hidden'),
    backgroundColor: tokens.colorNeutralStroke2,
  },
  spacerLineCurrent: {
    backgroundColor: tokens.colorBrandStroke2,
  },
  nameText: {
    fontSize: tokens.fontSizeBase400,
  },
  levelText: {
    fontSize: tokens.fontSizeBase200,
  },
  iconWrap: {
    ...shorthands.borderRadius(tokens.borderRadiusCircular),
    backgroundColor: tokens.colorNeutralBackground4,
    width: '50px',
    height: '50px',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
  },
  wrapPrevious: {
    backgroundColor: tokens.colorBrandBackground2,
  },
  wrapCurrent: {
    backgroundColor: tokens.colorBrandBackground,
  },
});
